import { FormProps } from '@alphabait/ui/form'
import { Control, UseFormRegister, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { AlphaTypes, TextareaInput } from './custom-types'
import styles from './form.module.css'
import { cn } from '@alphabait/ui/lib/utils'

// Legacy mode props
type LegacyProps = {
  field: TextareaInput
  formProps: FormProps
}

// Direct mode props (react-hook-form)
type DirectProps = {
  name: string
  control: Control<any>
  register?: UseFormRegister<any>
  placeholder?: string
  disabled?: boolean
  testId?: string
  minLength?: number
  maxLength?: number
  required?: boolean
  rows?: number
  className?: string
}

type CustomTextareaProps = LegacyProps | DirectProps

function isLegacyProps(props: CustomTextareaProps): props is LegacyProps {
  return 'field' in props && 'formProps' in props
}

export default function CustomTextarea(props: CustomTextareaProps) {
  const { t } = useTranslation()

  // Extract props based on mode
  const fieldName = isLegacyProps(props) ? props.field.name : props.name
  const control = isLegacyProps(props) ? props.formProps.control : props.control
  const minLength = isLegacyProps(props)
    ? props.field.validation?.minLength
    : props.minLength
  const maxLength = isLegacyProps(props)
    ? props.field.validation?.maxLength
    : props.maxLength
  const placeholder = isLegacyProps(props)
    ? props.field.uiOptions?.placeholder
    : props.placeholder
  const disabled = isLegacyProps(props)
    ? props.field.uiOptions?.disabled
    : props.disabled
  const testId = isLegacyProps(props) ? props.field.uiOptions?.testId : props.testId
  const rows = isLegacyProps(props) ? 4 : props.rows ?? 4
  const className = isLegacyProps(props)
    ? styles.customTextarea
    : props.className ?? styles.customTextarea

  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
  })

  return (
    <div className="w-full">
      <textarea
        rows={rows}
        className={cn(`${className} text-slate-50 border border-base-input bg-background-light rounded-md`)}
        placeholder={placeholder}
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        ref={ref}
        data-testid={testId}
        disabled={disabled}
      />
      {(minLength || maxLength) && (
        <div className="mt-1 flex justify-between text-xs text-gray-500">
          {minLength && <span>Min: {minLength}</span>}
          {maxLength && <span>Max: {maxLength}</span>}
        </div>
      )}
      {error && (
        <span className={styles.errorMessage} data-testid={`${testId || fieldName}-error`}>
          {t(`validation_messages.${error.message}`)}
        </span>
      )}
    </div>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const getCustomTextareaProperties = () =>
  [
    {
      name: 'uiOptions.label',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.question'),
        inputType: 'text',
      },
      validations: ['required'],
    },
    {
      name: 'validation.minLength',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.min_length'),
        inputType: 'number',
      },
    },
    {
      name: 'validation.maxLength',
      type: 'input',
      uiOptions: {
        label: i18n.t('questionnaire_builder.max_length'),
        inputType: 'number',
      },
    },
    {
      name: 'validation.required',
      customControlType: 'switch',
      type: 'custom',
      uiOptions: {
        label: i18n.t('questionnaire_builder.required'),
      },
    },
  ] as AlphaTypes[]
